/**
 * Analytics tracking for locked content interactions
 * Integrates with Segment + RudderStack via analyticsClient (no direct window.analytics usage)
 */

import { analyticsClient } from 'utils/analytics/client';

export interface LockedContentEventData {
  pageId: string;
  pageName?: string;
  scrollProgress?: number;
  lockPercentage?: number;
  email?: string;
  timeSpentSeconds?: number;
  [key: string]: string | number | boolean | undefined;
}

/**
 * Check if at least one analytics provider is available
 * (Segment OR Rudder). Consent gating happens inside analyticsClient.
 */
const isAnalyticsReady = (): boolean =>
  typeof window !== 'undefined' && (analyticsClient.isSegmentReady() || analyticsClient.isRudderReady());

/**
 * Helper: standard track wrapper with timestamp
 */
const trackEvent = (event: string, data: LockedContentEventData): void => {
  if (!isAnalyticsReady()) {
    console.warn(`[Analytics] Not ready - "${event}" not tracked`);

    return;
  }

  try {
    analyticsClient.track(event, {
      ...data,
      timestamp: new Date().toISOString(),
    });
  } catch (error) {
    console.error(`[Analytics] Failed to track "${event}":`, error);
  }
};

/**
 * Track scroll depth milestone
 * Fires at key percentages: 25%, 50%, 75%, 90%
 */
export const trackScrollDepth = (data: LockedContentEventData): void => {
  trackEvent('Locked Content - Scroll Depth', data);
};

/**
 * Track when user reaches the lock point
 */
export const trackLockReached = (data: LockedContentEventData): void => {
  trackEvent('Locked Content - Lock Reached', data);
};

/**
 * Track unlock attempt (when user submits Marketo form)
 */
export const trackUnlockAttempt = (data: LockedContentEventData): void => {
  trackEvent('Locked Content - Unlock Attempt', data);
};

/**
 * Track successful unlock
 * This is the main conversion event
 */
export const trackUnlockSuccess = (data: LockedContentEventData): void => {
  if (!isAnalyticsReady()) {
    console.warn('[Analytics] Not ready - unlock success not tracked');

    return;
  }

  try {
    // Identify user with email (goes to Segment + Rudder)
    if (data.email) {
      analyticsClient.identify(data.email, {
        email: data.email,
        lastUnlockedContent: data.pageId,
        lastUnlockedAt: new Date().toISOString(),
      });
    }

    analyticsClient.track('Locked Content - Unlock Success', {
      ...data,
      timestamp: new Date().toISOString(),
    });
  } catch (error) {
    console.error('[Analytics] Failed to track unlock success:', error);
  }
};

/**
 * Track PDF download click (before unlock)
 */
export const trackPDFDownloadAttempt = (data: LockedContentEventData): void => {
  trackEvent('Locked Content - PDF Download Attempt', data);
};

/**
 * Track PDF download (after unlock)
 */
export const trackPDFDownload = (data: LockedContentEventData): void => {
  trackEvent('Locked Content - PDF Download', data);
};

/**
 * Track quiz view (after unlock)
 */
export const trackQuizView = (data: LockedContentEventData): void => {
  trackEvent('Locked Content - Quiz View', data);
};

/**
 * Track quiz start (when user clicks "Take the quiz" button)
 */
export const trackQuizStart = (data: LockedContentEventData): void => {
  trackEvent('Locked Content - Quiz Start', data);
};

/**
 * Track time spent on page (pre vs post unlock)
 */
export const trackTimeSpent = (data: LockedContentEventData & { phase: 'pre-unlock' | 'post-unlock' }): void => {
  if (!isAnalyticsReady()) {
    console.warn('[Analytics] Not ready - time spent not tracked');

    return;
  }

  try {
    analyticsClient.track('Locked Content - Time Spent', {
      ...data,
      timestamp: new Date().toISOString(),
    });
  } catch (error) {
    console.error('[Analytics] Failed to track time spent:', error);
  }
};
